import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusCircle, ArrowRight, Info, Star, Menu, ChevronRight, TrendingDown, Zap, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { trackStarted } from '../../../utils/analytics';
import './AvailablePlans.css';

// Import mobile version
import AvailablePlansMobile from './mobile/AvailablePlans_mobile';

// Custom hook for responsive design
const useResponsiveDisplay = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    // Check if we're on the client-side
    if (typeof window !== 'undefined') {
      // Initial check
      const checkIfMobile = () => {
        // 768px is a common breakpoint for tablets, adjust as needed
        setIsMobile(window.innerWidth <= 768);
      };
      
      // Set initial value
      checkIfMobile();
      
      // Add event listener for window resize
      window.addEventListener('resize', checkIfMobile);
      
      // Cleanup
      return () => window.removeEventListener('resize', checkIfMobile);
    }
  }, []);
  
  return isMobile;
};

// EnhancedCarrierCard component with enhanced pricing and repositioned promotional text
const EnhancedCarrierCard = ({ carrier, index, isActive, onActivate }) => {
  const [showSlots, setShowSlots] = useState(false);
  const cardRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    
    setMousePosition({ x, y });
  };
  
  useEffect(() => {
    // Show slot bar after delay for nice animation sequence
    const timer = setTimeout(() => {
      setShowSlots(true);
    }, 300 + index * 150);
    
    return () => clearTimeout(timer);
  }, [index]);
  
  const slotPercentage = (carrier.slots / 1000) * 100;
  const slotStatus = slotPercentage > 70 ? 'high' : slotPercentage > 30 ? 'medium' : 'low';
  
  // Calculate savings
  const savings = carrier.originalPrice ? carrier.originalPrice - carrier.minPrice : null;
  const savingsPercentage = carrier.originalPrice ? Math.round(((carrier.originalPrice - carrier.minPrice) / carrier.originalPrice) * 100) : null;
  const annualSavings = savings ? savings * 12 : null;
  
  // Carrier icons based on carrier ID
  const getIconComponent = () => {
    switch(carrier.id) {
      case 'verizon':
        return (
          <div className="carrier-icon red">
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13 2L3 14h9l-1 8l10-12h-9l1-8z"></path>
            </svg>
          </div>
        );
      case 'att':
        return (
          <div className="carrier-icon blue">
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
            </svg>
          </div>
        );
      case 'google-fi':
        return (
          <div className="carrier-icon light-blue">
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12.55a11 11 0 0 1 14.08 0"></path>
              <path d="M1.42 9a16 16 0 0 1 21.16 0"></path>
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
              <line x1="12" y1="20" x2="12.01" y2="20"></line>
            </svg>
          </div>
        );
      case 'cricket':
        return (
          <div className="carrier-icon green">
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <line x1="21" y1="10" x2="3" y2="10"></line>
              <line x1="21" y1="6" x2="3" y2="6"></line>
              <line x1="21" y1="14" x2="3" y2="14"></line>
              <line x1="21" y1="18" x2="3" y2="18"></line>
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <motion.div 
      ref={cardRef}
      className={`available-plans-card enhanced ${carrier.color} ${isActive ? 'active' : ''}`}
      initial={{ opacity: 0, y: 30 }}
      animate={{ 
        opacity: 1, 
        y: 0, 
        boxShadow: isActive 
          ? '0 20px 40px rgba(0, 0, 0, 0.2), 0 0 30px rgba(255, 255, 255, 0.05) inset'
          : '0 10px 30px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05)'
      }}
      transition={{ 
        duration: 0.5, 
        delay: index * 0.1,
        type: 'spring',
        stiffness: 100,
        damping: 15
      }}
      whileHover={{ 
        y: -10, 
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)'
      }}
      onMouseMove={handleMouseMove}
      onClick={() => onActivate(carrier.id)}
      style={{
        transform: isActive 
          ? `perspective(1000px) rotateX(${mousePosition.y * 5}deg) rotateY(${mousePosition.x * 5}deg)` 
          : 'none'
      }}
    >
      <div className="card-content">
        {getIconComponent()}
        
        <h3 className="available-plans-carrier-name">{carrier.name} Plans</h3>
        
        <div className="available-plans-color-bar">
          <div className={`color-indicator ${carrier.color}`}></div>
        </div>
        
        <div className="available-plans-slot-indicator">
          <motion.div 
            className={`available-plans-slot-bar ${slotStatus}`}
            initial={{ width: 0 }}
            animate={{ width: showSlots ? `${slotPercentage}%` : '0%' }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        </div>
        
        <p className="available-plans-slot-info">
          <motion.span 
            className="available-plans-slot-number"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 + 0.5, duration: 0.5 }}
          >
            {carrier.slots}+
          </motion.span>
          <span className="available-plans-slot-text">Available Spots</span>
          <Info className="available-plans-info-icon" size={16} />
        </p>
        
        {/* ENHANCED PRICING SECTION */}
        <motion.div 
          className={`enhanced-pricing-container ${carrier.color}`}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: index * 0.1 + 0.4, duration: 0.5 }}
        >
          {/* Main Pricing Display */}
          <div className="pricing-showcase">
            {/* Original Price (Strikethrough) - Clean version */}
            {carrier.originalPrice && (
              <div className="original-price-display">
                <div className="retail-price-label">
                  <span className="retail-text">Retail Price</span>
                  <span className="per-line-text">per line</span>
                </div>
                <div className="original-price-wrapper">
                  <span className="original-currency">$</span>
                  <span className="original-amount">{carrier.originalPrice}</span>
                  <span className="original-period">/mo</span>
                  <div className="clean-strikethrough"></div>
                </div>
              </div>
            )}
            
            {/* Current Price - The Hero */}
            <div className="current-price-hero">
              <div className="circle-price-label">
                <span className="circle-text">Circle Price</span>
                <span className="verified-badge">
                  <Zap size={12} />
                  VERIFIED
                </span>
              </div>
              
              <div className="price-main">
                <span className="currency-symbol">$</span>
                <span className="price-amount">{carrier.minPrice}</span>
                <span className="price-period">/month</span>
              </div>
            </div>
            
            {/* Detailed Savings Breakdown */}
            {savings && (
              <div className="savings-breakdown">
                <div className="savings-row monthly-savings">
                  <div className="savings-icon">
                    <TrendingDown size={16} />
                  </div>
                  <div className="savings-details">
                    <span className="savings-amount">${savings}</span>
                    <span className="savings-label">saved monthly</span>
                  </div>
                  <div className="savings-percentage">
                    {savingsPercentage}% OFF
                  </div>
                </div>
                
                {annualSavings && (
                  <div className="savings-row annual-savings">
                    <div className="savings-icon annual-icon">
                      <Sparkles size={16} />
                    </div>
                    <div className="savings-details">
                      <span className="savings-amount annual">${annualSavings}</span>
                      <span className="savings-label">saved annually</span>
                    </div>
                    <div className="impact-badge">
                      HUGE IMPACT
                    </div>
                  </div>
                )}
                
                {/* Value Proposition */}
                <div className="value-proposition">
                  <div className="value-icon">💰</div>
                  <span className="value-text">
                    Same premium service, group pricing power
                  </span>
                </div>
              </div>
            )}
          </div>
          
          {/* Simplified Background Effects */}
          <div className="pricing-bg-effects">
            <div className="pricing-orb pricing-orb-1"></div>
            <div className="pricing-orb pricing-orb-2"></div>
            <div className="pricing-shine-sweep"></div>
          </div>
        </motion.div>
        
        {/* REPOSITIONED: Enhanced promotional deal section - now positioned after savings */}
        <div className="available-plans-deal-container">
          <motion.p 
            className="available-plans-carrier-deal"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 + 0.6, duration: 0.5 }}
          >
            <span className="deal-highlight">🎉</span>
            {carrier.deal}
          </motion.p>
        </div>
        
        <motion.button 
          className="available-plans-view-plans-btn enhanced-cta"
          whileHover={{ 
            scale: 1.05,
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
          }}
          whileTap={{ scale: 0.98 }}
          onClick={(e) => {
            e.stopPropagation();
            trackStarted(carrier.name);
            window.location.href = `/all-circles?carrier=${encodeURIComponent(carrier.name)}`;
          }}
        >
          <Zap size={18} />
          Explore Plans <ChevronRight className="btn-icon" size={18} />
          <div className="btn-shine"></div>
        </motion.button>
        
        {carrier.isPopular && (
          <motion.div 
            className="available-plans-popular-tag"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 + 0.6, duration: 0.5 }}
          >
            Top Choice
          </motion.div>
        )}
      </div>
      
      <div className="card-glow"></div>
      <div className="card-reflection"></div>
    </motion.div>
  );
};

// CreateCircleCard component (no changes)
const CreateCircleCard = () => {
  const navigate = useNavigate();
  
  return (
    <motion.div 
      className="available-plans-post-circle-card enhanced"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.5 }}
      whileHover={{ y: -10 }}
    >
      <div className="glow-effect"></div>
      
      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.5 }}
      >
        Create Your Own Circle
      </motion.h3>
      
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.5 }}
      >
        Start saving with your custom group plan
      </motion.p>
      
      <motion.div 
        className="circle-btn-container"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <motion.button 
          className="available-plans-post-circle-btn"
          animate={{ 
            boxShadow: [
              "0 0 0 0 rgba(255, 255, 255, 0.7)",
              "0 0 0 20px rgba(255, 255, 255, 0)",
              "0 0 0 0 rgba(255, 255, 255, 0)"
            ]
          }}
          transition={{ 
            duration: 2, 
            repeat: Infinity,
            repeatType: "loop" 
          }}
          onClick={() => navigate('/onboarding')}
        >
          <PlusCircle size={32} />
        </motion.button>
        
        <div className="button-particles">
          {Array.from({ length: 5 }).map((_, i) => (
            <motion.div
              key={i}
              className="button-particle"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ 
                scale: [0, 1, 0],
                opacity: [0, 0.5, 0],
                x: [0, (i % 2 ? 1 : -1) * (Math.random() * 60 + 20)],
                y: [0, (i % 2 ? 1 : -1) * (Math.random() * 60 + 20)]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "loop",
                delay: i * 0.3,
                ease: "easeOut"
              }}
            />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Enhanced testimonials with animation (no changes)
const EnhancedTestimonials = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const testimonials = [
    { id: 1, text: "Great way to get premium plans at a fraction of the cost. Customer service was helpful throughout the process.", author: "Emily R." },
    { id: 2, text: "I saved $30 a month on my phone bill with Circledin! Joining was easy and the service has been fantastic.", author: "Sarah K." },
    { id: 3, text: "I never thought group plans could be so simple. Circledin made it happen and saved me hundreds of dollars a year!", author: "Alex P." },
    { id: 4, text: "The customer service is top-notch. They helped me find the perfect circle for my needs and budget.", author: "Laura M." },
    { id: 5, text: "Joining a circle was easy and the savings are real. Highly recommend for anyone looking to cut their phone bill!", author: "Mike T." }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div className="enhanced-testimonial-container">
      <div className="quote-mark">"</div>
      
      <AnimatePresence mode="wait">
        <motion.div 
          key={testimonials[currentTestimonial].id}
          className="testimonial-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
        >
          <p className="testimonial-text">{testimonials[currentTestimonial].text}</p>
          <div className="testimonial-author">
            <div className="author-glow"></div>
            <span>- {testimonials[currentTestimonial].author}</span>
          </div>
        </motion.div>
      </AnimatePresence>
      
      <div className="testimonial-nav">
        {testimonials.map((_, i) => (
          <motion.button
            key={i}
            className={`nav-dot ${i === currentTestimonial ? 'active' : ''}`}
            onClick={() => setCurrentTestimonial(i)}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          />
        ))}
      </div>
    </div>
  );
};

// Desktop Version Component
const DesktopAvailablePlans = ({ carriers }) => {
  const [activeCarrier, setActiveCarrier] = useState(null);
  const [showAllPlans, setShowAllPlans] = useState(true);
  const [animateCards, setAnimateCards] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const navigate = useNavigate();
  
  // Detect section visibility for scroll-based animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setTimeout(() => {
            setAnimateCards(true);
          }, 300);
        }
      },
      { threshold: 0.1 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  
  const togglePlans = (showAll) => {
    setAnimateCards(false);
    setTimeout(() => {
      setShowAllPlans(showAll);
      setAnimateCards(true);
    }, 300);
  };

  const handleActivateCarrier = (id) => {
    setActiveCarrier(activeCarrier === id ? null : id);
  };
  
  const handleExploreAllCircles = () => {
    navigate('/all-circles');
  };
  
  // Dynamic background particles for enhanced visual design
  const renderParticles = () => {
    return Array.from({ length: 30 }).map((_, i) => (
      <div 
        key={i}
        className="background-particle"
        style={{
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          width: `${Math.random() * 5 + 3}px`,
          height: `${Math.random() * 5 + 3}px`,
          animationDelay: `${Math.random() * 5}s`,
          animationDuration: `${Math.random() * 10 + 10}s`
        }}
      />
    ));
  };

  return (
    <section className="available-plans-section enhanced" ref={sectionRef}>
      <div className="background-particles">
        {renderParticles()}
      </div>
      <div className="gradient-overlay"></div>
      
      <div className="section-inner">
        <div className="available-plans-header">
          <div className="header-content">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
              transition={{ duration: 0.5 }}
              className="animated-heading"
            >
              <span className="gradient-text">Unlock Premium Plans</span> at Group Rates
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              style={{ textAlign: 'left' }}
            >
              Join a circle or create your own to access exclusive discounts on top-tier phone plans
            </motion.p>
          </div>
          
          <motion.button 
            className="available-plans-view-all-btn enhanced"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.9 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            whileHover={{ 
              scale: 1.05, 
              boxShadow: '0 15px 30px rgba(255, 51, 102, 0.4)' 
            }}
            whileTap={{ scale: 0.98 }}
            onClick={handleExploreAllCircles}
          >
            Explore All Circles <ChevronRight size={18} />
          </motion.button>
        </div>

        <div className="available-plans-controls" role="radiogroup" aria-label="Filter plans">
          <motion.div 
            className={`available-plans-toggle-container ${showAllPlans ? '' : 'popular-active'}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <button
              className={`available-plans-toggle-option ${showAllPlans ? 'active' : ''}`}
              onClick={() => togglePlans(true)}
              role="radio"
              aria-checked={showAllPlans}
              tabIndex={showAllPlans ? 0 : -1}
            >
              <Menu size={16} /> All Plans
            </button>
            <button
              className={`available-plans-toggle-option ${!showAllPlans ? 'active' : ''}`}
              onClick={() => togglePlans(false)}
              role="radio"
              aria-checked={!showAllPlans}
              tabIndex={!showAllPlans ? 0 : -1}
            >
              <Star size={16} /> Featured Plans
            </button>
            <div className="available-plans-slider"></div>
          </motion.div>
        </div>

        <div 
          className={`available-plans-grid ${animateCards ? 'animate' : ''}`} 
          id={showAllPlans ? "all-plans" : "featured-plans"} 
          role="tabpanel"
        >
          {carriers
            .filter(carrier => showAllPlans || carrier.isPopular)
            .map((carrier, index) => (
              <EnhancedCarrierCard 
                key={carrier.id}
                carrier={carrier}
                index={index}
                isActive={activeCarrier === carrier.id}
                onActivate={handleActivateCarrier}
              />
            ))}
        </div>
        
        {/* Clear divider to ensure separation */}
        <div style={{ clear: 'both', width: '100%', height: '4rem' }}></div>
        
        {/* Bottom section with side-by-side elements */}
        <motion.div 
          className="bottom-section-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <CreateCircleCard />
          <EnhancedTestimonials />
        </motion.div>
        
        {/* Additional space at the bottom of the section */}
        <div style={{ clear: 'both', width: '100%', height: '4rem' }}></div>
      </div>
    </section>
  );
};

// Main Component - Updated with enhanced carrier data including originalPrice
const AvailablePlans = () => {
  // All hooks must be at the top level
  const isMobile = useResponsiveDisplay();
  
  // Updated carrier data with originalPrice for enhanced pricing display
  const carriers = [
    { 
      id: "verizon", 
      name: 'Verizon', 
      deal: 'Up to $540 bonus credit, plans from $25/mo', 
      minPrice: 25, 
      originalPrice: 80, // Added original price for savings calculation
      slots: 780, 
      color: 'red', 
      isPopular: true 
    },
    { 
      id: "att", 
      name: 'AT&T', 
      deal: 'No activation fee ($35 value) for new lines', 
      minPrice: 30, 
      originalPrice: 85, // Added original price for savings calculation
      slots: 430, 
      color: 'blue' 
    },
    { 
      id: "google-fi", 
      name: 'Google Fi', 
      deal: 'Earn $60 for referring new members', 
      minPrice: 20, 
      originalPrice: 70, // Added original price for savings calculation
      slots: 210, 
      color: 'light-blue' 
    },
    { 
      id: "cricket", 
      name: 'Cricket', 
      deal: 'Flexible plans, no annual commitments', 
      minPrice: 20, 
      originalPrice: 60, // Added original price for savings calculation
      slots: 150, 
      color: 'green' 
    },
  ];
  
  // Now conditionally render the appropriate version
  return isMobile ? (
    <AvailablePlansMobile />
  ) : (
    <DesktopAvailablePlans carriers={carriers} />
  );
};

export default AvailablePlans;